'use client';

import { Box, Button, Flex, Spinner, Text } from '@chakra-ui/react';
import { AiOutlineUser } from 'react-icons/ai';
import { IoKeyOutline } from 'react-icons/io5';
import { useCallback, useContext, useState } from 'react';
import { AuthContext } from '@/contexts/AuthContext';
import { z } from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ErrorMessage from '@/components/ErrorMessage';
import FormInput from '@/components/Form/form-input';

type FormObject = {
  email: string;
  password: string;
};

const schema = z.object({
  email: z.email('E-mail inválido').min(1, { message: 'E-mail é obrigatório' }),
  password: z
    .string({ message: 'Palavra-passe é obrigatório' })
    .min(1, { message: 'Palavra-passe é obrigatório' }),
});

const UserSignIn = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { handleChangeStatus, signIn } = useContext(AuthContext);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormObject>({ resolver: zodResolver(schema) });

  const onSubmit = useCallback(
    async (data: FormObject) => {
      setIsLoading(true);
      try {
        await signIn(data.email, data.password, () => setIsLoading(false));

        reset();
      } catch (error) {
        const e = error as { message: string };

        setErrorMessage(e.message);
      } finally {
        setIsLoading(false);
      }
    },
    [reset, signIn],
  );

  return (
    <Flex
      flexDir="column"
      gap="16px"
      as="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Flex flexDir="column" gap="2px" pb="2px">
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.8px"
          textTransform="uppercase"
          color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.35)' }}
        >
          Área de Cliente
        </Text>
        <Text
          fontSize="18px"
          fontWeight="700"
          color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
        >
          Iniciar Sessão
        </Text>
      </Flex>
      <Flex>
        <FormInput
          placeholder="E-mail"
          width="100%"
          error={errors.email?.message}
          {...register('email')}
          icon={<AiOutlineUser />}
        />
      </Flex>
      <Flex>
        <Controller
          control={control}
          name="password"
          render={({ field }) => (
            <FormInput
              placeholder="Palavra-passe"
              width="100%"
              error={errors.password?.message}
              icon={<IoKeyOutline />}
              name={field.name}
              onChange={field.onChange}
              type="password"
            />
          )}
        />
      </Flex>

      <Text
        fontSize="12px"
        onClick={() => !isLoading && handleChangeStatus('recover_account')}
        color="#00B0CA"
        cursor="pointer"
        opacity="0.85"
        _hover={{ opacity: 1, textDecoration: 'underline' }}
        transition="all 0.15s"
        alignSelf="flex-end"
      >
        Esqueceste-te da palavra-passe?
      </Text>

      <Button
        w="full"
        type="submit"
        h="44px"
        borderRadius="full"
        bg="#00B0CA"
        color="white"
        fontWeight="700"
        fontSize="15px"
        boxShadow="0 4px 20px rgba(0,176,202,0.3)"
        disabled={isLoading}
        _hover={{ bg: '#009AB5' }}
        _disabled={{ opacity: 0.5 }}
        transition="all 0.2s"
      >
        {isLoading ? <Spinner size="sm" color="white" /> : 'Iniciar Sessão'}
      </Button>

      {errorMessage && <ErrorMessage message={errorMessage} />}

      <Box
        h="1px"
        bg={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
      />

      <Flex flexDir="column" align="center" gap="10px">
        <Text
          fontSize="13px"
          color={{ base: 'rgba(0,0,0,0.5)', _dark: 'rgba(255,255,255,0.4)' }}
        >
          Primeira visita?
        </Text>
        <Button
          w="full"
          h="40px"
          borderRadius="full"
          bg="transparent"
          border="1.5px solid"
          borderColor={{
            base: 'rgba(0,0,0,0.15)',
            _dark: 'rgba(255,255,255,0.12)',
          }}
          color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
          fontSize="14px"
          fontWeight="600"
          disabled={isLoading}
          onClick={() => !isLoading && handleChangeStatus('new_account')}
          _hover={{ bg: 'rgba(0,176,202,0.06)', borderColor: '#00B0CA', color: '#00B0CA' }}
          transition="all 0.2s"
        >
          Criar Conta
        </Button>
      </Flex>
    </Flex>
  );
};

export default UserSignIn;
